import React from 'react';
import { AnimatedSection } from '../components/AnimatedSection';
import { Card, CardContent } from '../components/ui/card';
import { PiggyBank, TrendingUp, Shield, Calculator, DollarSign, FileText } from 'lucide-react';

export const Plan401k = () => {
  const benefits = [
    {
      icon: DollarSign,
      title: 'Tax Advantages',
      description: 'Pre-tax contributions reduce your current taxable income while building retirement savings.',
    },
    {
      icon: TrendingUp,
      title: 'Compound Growth',
      description: 'Investments grow tax-deferred, allowing compound interest to accelerate your wealth.',
    },
    {
      icon: Shield,
      title: 'Employer Matching',
      description: 'Many employers match contributions, providing free money toward your retirement.',
    },
    {
      icon: Calculator,
      title: 'Flexible Contributions',
      description: 'Adjust contribution amounts based on your financial situation and goals.',
    },
    {
      icon: PiggyBank,
      title: 'Automatic Savings',
      description: 'Contributions are automatically deducted from your paycheck for effortless saving.',
    },
    {
      icon: FileText,
      title: 'Diverse Investment Options',
      description: 'Choose from various investment options to match your risk tolerance and timeline.',
    },
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-[#0B2A4A] via-[#0B2A4A] to-[#1a3d5f] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFD700] rounded-full filter blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#FFD700] rounded-full filter blur-3xl" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            401(k) Retirement Systems
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Building long-term financial security through intelligent retirement planning
          </p>
        </div>
      </section>

      {/* Main Content */}
      <AnimatedSection>
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
              <div>
                <h2 className="text-4xl font-bold text-[#0B2A4A] mb-6">
                  What is a 401(k)?
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  A 401(k) is a retirement savings plan sponsored by employers that allows employees to save and invest a portion of their paycheck before taxes are taken out.
                </p>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  This powerful retirement tool helps you build wealth over time through consistent contributions and potential employer matching. The funds are invested in various options such as mutual funds, stocks, and bonds.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  One of the key advantages is the tax-deferred growth - you don't pay taxes on the money until you withdraw it in retirement, allowing your investments to compound more effectively over decades.
                </p>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1732111816779-aeec50f788ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxmaW5hbmNpYWwlMjBpbm5vdmF0aW9ufGVufDB8fHx8MTc3MzczNTcwMHww&ixlib=rb-4.1.0&q=85"
                  alt="401k Retirement Savings"
                  className="rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#FFD700] rounded-full filter blur-3xl opacity-20" />
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Benefits Grid */}
      <AnimatedSection>
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0B2A4A] mb-6">
                Key Benefits
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Why 401(k) plans are essential for retirement security
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <Card className="group h-full bg-white border-2 border-transparent hover:border-[#FFD700] shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                    <CardContent className="p-8">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#0B2A4A] to-[#1a3d5f] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                        <benefit.icon className="w-8 h-8 text-[#FFD700]" />
                      </div>
                      <h3 className="text-xl font-bold text-[#0B2A4A] mb-4">
                        {benefit.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {benefit.description}
                      </p>
                    </CardContent>
                  </Card>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* How It Works */}
      <AnimatedSection>
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#0B2A4A] mb-6">
                How 401(k) Plans Work
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Understanding the mechanics of your retirement savings
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg border border-gray-100">
                <div className="w-12 h-12 bg-[#FFD700] rounded-lg flex items-center justify-center mb-6">
                  <span className="text-[#0B2A4A] text-2xl font-bold">1</span>
                </div>
                <h3 className="text-2xl font-bold text-[#0B2A4A] mb-4">Contribution Phase</h3>
                <p className="text-gray-600 leading-relaxed">
                  You decide what percentage of your salary to contribute. This amount is automatically deducted from your paycheck before taxes. Many employers offer matching contributions up to a certain percentage, effectively doubling your investment.
                </p>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg border border-gray-100">
                <div className="w-12 h-12 bg-[#FFD700] rounded-lg flex items-center justify-center mb-6">
                  <span className="text-[#0B2A4A] text-2xl font-bold">2</span>
                </div>
                <h3 className="text-2xl font-bold text-[#0B2A4A] mb-4">Investment Growth</h3>
                <p className="text-gray-600 leading-relaxed">
                  Your contributions are invested according to your chosen allocation strategy. The investments grow tax-deferred over time, with compound interest accelerating your wealth accumulation.
                </p>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg border border-gray-100">
                <div className="w-12 h-12 bg-[#FFD700] rounded-lg flex items-center justify-center mb-6">
                  <span className="text-[#0B2A4A] text-2xl font-bold">3</span>
                </div>
                <h3 className="text-2xl font-bold text-[#0B2A4A] mb-4">Vesting Schedule</h3>
                <p className="text-gray-600 leading-relaxed">
                  While your contributions are always yours, employer matching may follow a vesting schedule. This determines when employer contributions become fully yours, encouraging long-term employment.
                </p>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg border border-gray-100">
                <div className="w-12 h-12 bg-[#FFD700] rounded-lg flex items-center justify-center mb-6">
                  <span className="text-[#0B2A4A] text-2xl font-bold">4</span>
                </div>
                <h3 className="text-2xl font-bold text-[#0B2A4A] mb-4">Retirement Distribution</h3>
                <p className="text-gray-600 leading-relaxed">
                  Upon retirement (typically at age 59½ or later), you can begin withdrawing funds. These withdrawals are taxed as ordinary income, but ideally at a lower tax rate than during your working years.
                </p>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Planning Tips */}
      <AnimatedSection>
        <section className="py-24 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#0B2A4A] mb-6">
                Maximizing Your 401(k)
              </h2>
              <p className="text-xl text-gray-600">
                Expert strategies for optimal retirement savings
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-[#FFD700] hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-xl font-bold text-[#0B2A4A] mb-3">Capture Full Employer Match</h3>
                <p className="text-gray-600 leading-relaxed">
                  Always contribute enough to receive your employer's full match - it's essentially free money for your retirement.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-[#FFD700] hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-xl font-bold text-[#0B2A4A] mb-3">Increase Contributions Gradually</h3>
                <p className="text-gray-600 leading-relaxed">
                  Raise your contribution percentage annually, especially after raises or bonuses, to accelerate retirement savings.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-[#FFD700] hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-xl font-bold text-[#0B2A4A] mb-3">Diversify Your Investments</h3>
                <p className="text-gray-600 leading-relaxed">
                  Spread investments across different asset classes to balance risk and potential returns based on your timeline.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-[#FFD700] hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-xl font-bold text-[#0B2A4A] mb-3">Review and Rebalance Regularly</h3>
                <p className="text-gray-600 leading-relaxed">
                  Periodically review your portfolio allocation and rebalance to maintain your target investment mix.
                </p>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>
    </div>
  );
};

export default Plan401k;
